import React from 'react';
import { Link } from 'react-router-dom';
import { Warning as WarningIcon } from '@mui/icons-material';
import type { ReadinessCard } from '../../hooks/useKbReadiness';

interface ReadinessStripProps {
  cards: ReadinessCard[];
  loading: boolean;
}

/**
 * Five plain-language readiness cards — the first thing a returning admin
 * sees. Deliberately never mentions embeddings, FAISS, vector index, or
 * sync: those live as small secondary indicators elsewhere (e.g. the
 * "Preparing for live calls" chip in Answers), not here.
 */
const ReadinessStrip: React.FC<ReadinessStripProps> = ({ cards, loading }) => {
  if (loading) {
    return (
      <div className="kb-readiness-grid grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-20 rounded-card border border-hairline bg-surface-subtle animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="kb-readiness-grid grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {cards.map((card) => {
        const isReady = card.status === 'ready' || card.status === 'draft_ready';
        const needsMoreDetail = card.status === 'needs_more_detail';
        const content = <>
          {!isReady && (
            <div className={`flex items-center gap-1.5 text-xs font-semibold ${needsMoreDetail ? 'text-warning' : 'text-error'}`}>
              <WarningIcon style={{ fontSize: '1rem' }} />
              {needsMoreDetail ? 'Add details' : 'Review'}
            </div>
          )}
          <div className={`${isReady ? '' : 'mt-1.5'} text-sm font-semibold text-ink`}>{card.label}</div>
          <div className="mt-0.5 text-xs text-ink-muted">{card.detail}</div>
        </>;

        return <Link key={card.key} to={card.href || `/knowledge-base?tab=${card.key === 'training' ? 'overview' : card.key}`} className={`kb-readiness-card group flex h-full flex-col cursor-pointer rounded-card border p-3.5 transition-all focus:outline-none focus:ring-2 focus:ring-signature ${isReady ? 'border-success bg-surface' : needsMoreDetail ? 'border-warning bg-warning-tint' : 'border-error bg-error-tint'}`} aria-label={`Open ${card.label}: ${card.detail}`}>
          {content}
          <div className="mt-auto pt-2 text-xs font-semibold text-signature opacity-80 group-hover:opacity-100">Open</div>
        </Link>;
      })}
    </div>
  );
};

export default ReadinessStrip;
